// Image URLs, exactly as the legacy app built them (.ai/rewrite-notes.md 2.8). The sounds are made in code (sound/synth.ts).
import { env } from '../env';

/** `?v=5`: legacy Solitaire.IMG_REVISION was 4; 5 since the baked edge shadow was removed from the modern / classic / strips pictures (2026-10-18, e2e/tools/card-edges). Theme previews have no revision. */
// Raise it whenever a card, back, background or empty-slot picture changes (and the `?v=` in server/views/_base.html): with a `?v=` they are kept a year by browsers and Cloudflare
// (server/lib/static.js keptForAYear, 2026-10-25).
export const IMG_REVISION = '?v=5';

/** WebP pictures (owner 2026-10-26): every card face, back and background has a .webp next to its PNG / JPG
 *  (frontend/scripts/make-webp.sh), 3.5x smaller. `cards` is set once at the start, before any picture is named, from the
 *  page template's test (window.__webpReady, app-prelude.ts); the background follows the choice the template's inline
 *  script made before that answer came (window.__bgWebp), so it is downloaded once. PNG / JPG stay for a browser
 *  without WebP (Safari 14 on macOS 10.15) and the legacy game. */
const formats = { cards: false, background: false };
export function setPictureFormats(cards: boolean, background: boolean): void {
  formats.cards = cards;
  formats.background = background;
}
const pic = (): string => (formats.cards ? 'webp' : 'png');
/** The WebP card faces and backs: `?v=7` (`?v=6` was poisoned in the deploy of 2026-10-27: during Render's switch-over
 *  some requests for the new address still reached the old server, and Cloudflare kept its lossy file for a year under
 *  it; a new address and the changed files must not go out in the same deploy, see .ai/gotchas.md). The 1x ones
 *  are lossless since then (owner 2026-10-27: on a 1x screen the lossy q93 ones blurred the red signs, WebP keeps colour
 *  at half resolution; the x2 ones stay lossy). The PNG and the backgrounds keep IMG_REVISION. */
const WEBP_REVISION = '?v=7';
const rev = (): string => (formats.cards ? WEBP_REVISION : IMG_REVISION);

/** Decks and backs with an `x2/` (HD) folder. */
export const hasHd = (name: string): boolean => /^(classic|modern|strips)/.test(name);

export const faceSrc = (deck: string, id: string, hd = false): string =>
  `${env.ss}images/cards/${deck}/${hd ? 'x2/' : ''}${id}.${pic()}${rev()}`;
export const backSrc = (back: string, hd = false): string =>
  `${env.ss}images/backs/${back}/${hd ? 'x2/' : ''}preview.${pic()}${rev()}`;
/** Mahjong's tiles (frontend/scripts/tiles/make-tiles.mjs): a set in the style of each card deck (owner 2026-10-31; before
 *  that the retro set whatever the deck), `tiles/<set>`, where the set is the deck option without its `_v1` / `_v2`
 *  (the retro deck classic_old is the `retro` set) and an unknown deck gets the retro set; or the set the player picked
 *  on the Themes window's Advanced tab (skin.tileType). `back` is the tile's back. Board cardRecords passes the tile deck
 *  as the deck of a tile; CardEl tells a tile by it (isTileDeck). */
const TILE_SETS: Record<string, string> = { classic_old: 'retro', modern: 'modern', classic: 'classic', strips: 'strips', honeycomb: 'honeycomb', animals: 'animals' };
/** The sets in the order of the decks (ThemesWindow DECKS), for the Advanced tab's arrows. */
export const TILE_SET_NAMES = Object.values(TILE_SETS);
export const tileSetOf = (deckType: string, tileType?: string): string =>
  (tileType && TILE_SET_NAMES.includes(tileType) ? tileType : TILE_SETS[deckType.replace(/_v\d+$/, '')]) ?? 'retro';
export const tileDeckOf = (deckType: string, tileType?: string): string => `tiles/${tileSetOf(deckType, tileType)}`;
export const isTileDeck = (deck: string): boolean => deck.startsWith('tiles/');
/** Raise it whenever make-tiles.mjs draws the tiles again (the pictures are cached for a day). */
export const TILE_REVISION = '?t=5';
/** `deck`: a tile deck (tileDeckOf). `flat`: the set without the painted shadow (frontend/scripts/tiles/make-tiles.mjs),
 *  for the tiles off the layout. */
export const tileSrc = (deck: string, id: string, hd = false, flat = false): string =>
  `${env.ss}images/${deck}/${flat ? 'flat/' : ''}${hd ? 'x2/' : ''}${id}.webp${TILE_REVISION}`;
export const tileSrcSet = (deck: string, id: string, flat = false): string =>
  `${tileSrc(deck, id, false, flat)} 1x, ${tileSrc(deck, id, true, flat)} 2x`;
/** The background and the empty slot carry IMG_REVISION too (2026-10-25): without a version they were kept only a day,
 *  and a returning player's start waited for their check on a slow line. server/views/_base.html sets the same
 *  background address before the app loads (keep the two alike). */
export const emptySlotSrc = (background: string): string =>
  `${env.ss}images/backgrounds/${background}/empty.png${IMG_REVISION}`;
export const backgroundSrc = (background: string, mobile = false): string =>
  `${env.ss}images/backgrounds/${background}/background${mobile ? '_mobile' : ''}.${formats.background ? 'webp' : 'jpg'}${IMG_REVISION}`;
export const previewSrc = (background: string): string => `${env.ss}images/backgrounds/${background}/preview.jpg`;
/** Raise it whenever the Games window thumbnails are made again (e2e/tools/game-previews): without it a browser and
 *  Cloudflare kept the old picture a day (owner 2026-10-27, the Mahjong one in the bright-tile look). Not `?v=`: that
 *  keeps a picture a year (server/lib/static.js keptForAYear), and a new address sent in the same deploy as a changed
 *  file can be poisoned for that year by the old server during the switch-over (.ai/gotchas.md). */
const GAME_PREVIEW_REVISION = '?t=2';
export const gamePreviewSrc = (gameId: string): string =>
  `${env.ss}images/games/${gameId.replace(':', '-')}.jpg${GAME_PREVIEW_REVISION}`;
/** The Games window thumbnails at 2x / 3x for Retina screens (made by e2e/tools/game-previews). */
export const gamePreviewSrcSet = (gameId: string): string => {
  const base = `${env.ss}images/games/${gameId.replace(':', '-')}`;
  return `${base}.jpg${GAME_PREVIEW_REVISION} 1x, ${base}@2x.jpg${GAME_PREVIEW_REVISION} 2x, ${base}@3x.jpg${GAME_PREVIEW_REVISION} 3x`;
};
export const flagSrc = (code: string): string => `${env.ss}images/flags/${code}.png`;

/** The codes with no SVG flag (deprecated countries): their PNG stays. "01", the unknown country, has a grey SVG with
 *  a question mark (owner 2026-10-31). */
const FLAG_PNG_ONLY = new Set(['an', 'cs']);
/** The country code of a flag path the server gives (`images/flags/<cc>.png`), or null. */
export const flagCode = (src: string): string | null => /images\/flags\/([a-z0-9]{2})\.png$/.exec(src)?.[1] ?? null;
/** The SVG flag (public_html/images/flags/svg, from flag-icons, MIT; 2026-10-31: the PNGs are 16 x 11 and blur on
 *  every retina screen), or the PNG itself when the code has none. */
export const sharpFlag = (src: string): string => {
  const code = flagCode(src);
  return code && !FLAG_PNG_ONLY.has(code) ? `${env.ss}images/flags/svg/${code}.svg` : src;
};
/** The <img> of a flag for the HTML strings (the player's row, the ranking cells): the SVG at the PNG's 16 x 11 (a 4:3
 *  flag squeezed by 3 %, unseen), or the plain PNG tag the texts always had. */
export const flagImgHtml = (src: string): string => {
  const sharp = sharpFlag(src);
  return sharp === src ? `<img src="${src}" alt="">` : `<img src="${sharp}" alt="" width="16" height="11">`;
};

/** `srcset` with the HD candidate when the deck/back has one. */
/** PROTOTYPE: `?bigcards=1` lifts the card limit on big screens (board/geometry.ts BIG_CARD_MAX_W) and always
 *  loads the 2x images (218x288): the browser picks a srcset 1x / 2x by the screen's pixel ratio only, so a
 *  180 px card on a 1x screen got the 109 px image, stretched. */
export const bigCardsProto = (): boolean =>
  typeof location !== 'undefined' && new URLSearchParams(location.search).has('bigcards');
export const faceSrcSet = (deck: string, id: string): string | undefined =>
  hasHd(deck) && !bigCardsProto() ? `${faceSrc(deck, id)} 1x, ${faceSrc(deck, id, true)} 2x` : undefined;
export const backSrcSet = (back: string): string | undefined =>
  hasHd(back) && !bigCardsProto() ? `${backSrc(back)} 1x, ${backSrc(back, true)} 2x` : undefined;

/** An image to preload: a URL, or a URL with the same `srcset` the page will use. */
export type PreloadImage = string | { src: string; srcSet?: string | undefined };

/** Preload a list of images; resolves when all have loaded or failed (a failure never blocks), true then, or with
 *  false when `timeoutMs` ran out first (the start report tells it, the page template's watchdog). An entry
 *  with a `srcSet` is loaded through it, so the browser picks the same file as the card's <img>: on a
 *  Retina screen (iPhone, iPad, Mac) that is the `x2/` image, and preloading only the 1x file left every
 *  card face to load when it first turned up (owner 2026-10-18: "obrazek karty się doładowuje"). */
/** The pictures that have come (their `src`): a card face that is not among them yet shows a plain face while it loads
 *  (board/CardEl.tsx, the first deal no longer waits for every face; slow start reports of 2026-10-29). */
const arrived = new Set<string>();
export const hasArrived = (src: string): boolean => arrived.has(src);
/** The paper of each deck's faces (sampled from its 2 of clubs, 2026-10-29): a face still loading is a blank card of
 *  the same deck, opaque, so the cards under it stay covered (owner 2026-10-29: not white, not see-through). */
const PAPER: Record<string, string> = {
  classic_old: '#f4e5ca',
  classic: '#eaeaea',
  animals: '#ebebeb',
  modern: '#e8e8ea',
  honeycomb: '#e6ebed',
  strips: '#dfdfdf',
  modern_v2: '#000000',
};
export const paperOf = (deck: string): string => PAPER[deck] ?? PAPER[deck.replace(/_v[12]$/, '')] ?? '#ebebeb';
export const markArrived = (src: string): void => void arrived.add(src);

export function preloadImages(items: PreloadImage[], timeoutMs = 15_000, pending?: Set<string>): Promise<boolean> {
  return new Promise((resolve) => {
    let left = items.length;
    if (!left) return resolve(true);
    const done = () => {
      if (--left === 0) resolve(true);
    };
    const t = setTimeout(() => resolve(false), timeoutMs);
    for (const it of items) {
      const img = new Image();
      // `pending` holds the pictures that have not come yet (the start watchdog names them in its report)
      const name = typeof it === 'string' ? it : it.src;
      pending?.add(name);
      img.onload = img.onerror = (e) => {
        if ((e as Event).type === 'load') arrived.add(name); // a failed one keeps the plain face
        pending?.delete(name);
        done();
        if (left === 0) clearTimeout(t);
      };
      if (typeof it === 'string') img.src = it;
      else {
        if (it.srcSet) img.srcset = it.srcSet; // before src, so only the chosen candidate is fetched
        img.src = it.src;
      }
    }
  });
}

/** The 52 faces of a deck and a back, as the cards show them (`srcset` included). */
/** Mahjong's pictures, both sets (the flat ones show on the deck while the tiles gather: not loaded yet, a tile would
 *  blink as it reached the deck). */
export const tileImages = (deck: string, ids: readonly string[]): PreloadImage[] =>
  [...ids, 'back'].flatMap((id) =>
    [false, true].map((flat) => ({ src: tileSrc(deck, id, false, flat), srcSet: tileSrcSet(deck, id, flat) })),
  );
export const faceImages = (deck: string, ids: string[]): PreloadImage[] =>
  ids.map((id) => ({ src: faceSrc(deck, id), srcSet: faceSrcSet(deck, id) }));
export const backImage = (back: string): PreloadImage => ({ src: backSrc(back), srcSet: backSrcSet(back) });
